'use client';

import { motion } from "framer-motion";
import Link from "next/link";
import { ArrowLeft, CheckCircle, Shield, AlertTriangle, FileCheck, Search } from "lucide-react";
import { useParams } from "next/navigation";

const complianceScannerData = {
  title: "Compliance Scanner Platform",
  subtitle: "Automated regulatory compliance monitoring and risk assessment system.",
  headerGradient: "bg-gradient-to-r from-red-500 via-orange-600 to-yellow-600",
  accentColor: "text-red-600",
  buttonColor: "bg-red-600 hover:bg-red-700",
  challenge: "Organizations face increasing regulatory complexity and manual compliance processes that are time-consuming, error-prone, and difficult to scale. Traditional compliance methods struggle to keep pace with evolving regulations.",
  solutionHighlights: [
    "Developed automated scanning system for real-time compliance monitoring across multiple frameworks",
    "Implemented intelligent document analysis and regulatory mapping capabilities",
    "Created risk scoring algorithms to prioritize compliance issues by severity and impact",
    "Built comprehensive reporting dashboard with audit trails and evidence collection"
  ],
  benefits: [
    "80% reduction in compliance assessment time",
    "95% improvement in regulatory violation detection",
    "70% decrease in manual audit preparation effort",
    "Real-time compliance status monitoring"
  ],
  techStack: ["Document Analysis", "Regulatory AI", "Risk Assessment", "Automated Scanning", "Compliance Framework", "Audit Trail"],
  scannerStats: {
    documentsScanned: "12,847",
    complianceScore: "98.2%",
    violationsFound: "3",
    riskLevel: "Low"
  }
};

export default function ComplianceScannerPage() {
  const params = useParams();
  const currentLocale = (params?.locale as string) || 'en';
  
  return (
    <div className="min-h-screen bg-slate-50 text-slate-800">
      {/* Header Section */}
      <header className={`py-16 md:py-24 text-white ${complianceScannerData.headerGradient}`}>
        <div className="container mx-auto max-w-4xl px-4 text-center">
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
            <Link href={`/${currentLocale}/portfolio`} className="inline-flex items-center text-sm text-white hover:text-yellow-100 transition-colors duration-300 bg-white/10 hover:bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full shadow-md">
              <ArrowLeft size={18} className="mr-2" />
              Back to Portfolio
            </Link>
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
            {complianceScannerData.title}
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="text-lg md:text-xl text-yellow-100 max-w-3xl mx-auto">
            {complianceScannerData.subtitle}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="mt-8 flex flex-wrap gap-3 justify-center">
            {complianceScannerData.techStack.slice(0, 3).map(tech => (
              <span key={tech} className="bg-white/20 text-white px-3 py-1 rounded-full text-xs font-medium">
                {tech}
              </span>
            ))}
          </motion.div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="container mx-auto max-w-5xl px-4 py-12 md:py-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white p-8 md:p-12 rounded-xl shadow-xl -mt-20 md:-mt-32 relative z-10">
          
          {/* Compliance Scanner Dashboard Visual Section */}
          <section className="mb-12">
            <h2 className={`text-3xl font-semibold mb-8 text-center ${complianceScannerData.accentColor}`}>Compliance Control Center</h2>
            <div className="bg-slate-100 p-4 sm:p-6 rounded-lg shadow-lg border border-slate-200">
              <div className="flex items-center justify-between px-4 py-2 mb-4 bg-gradient-to-r from-slate-700 to-slate-800 text-white text-sm font-semibold rounded-t-md shadow">
                <span>Compliance Scanner Interface</span>
                <span className="text-xs bg-red-500/90 px-2 py-0.5 rounded-full flex items-center">
                  <Search size={12} className="mr-1"/>
                  Scanning
                </span>
              </div>
              <div className="flex flex-col md:flex-row gap-4 min-h-[280px]">
                {/* Scanner Interface Simulation */}
                <div className="flex-1 flex items-center justify-center bg-slate-200 rounded-md p-4 relative min-h-[200px]">
                  <div className="w-full h-full bg-slate-800 rounded-lg relative overflow-hidden shadow-inner border border-slate-700 flex flex-col p-4">
                    <div className="absolute top-2 left-2 right-2 flex justify-between items-center">
                      <div className="flex items-center">
                        <Shield size={18} className="text-red-400 mr-1" />
                        <span className="text-slate-200 font-medium">Compliance Monitor</span>
                      </div>
                      <div className="bg-red-900/50 text-red-200 px-2 py-0.5 rounded text-xs font-medium">
                        Live Scan
                      </div>
                    </div>
                    
                    <div className="mt-10 flex-1 relative">
                      {/* Simulated scanning interface */}
                      <div className="w-full h-full bg-slate-700 rounded relative p-4">
                        {/* Scan results */}
                        <div className="space-y-3 text-xs">
                          <div className="flex items-center justify-between p-2 bg-green-900/30 rounded border border-green-700">
                            <div className="flex items-center">
                              <FileCheck size={14} className="text-green-400 mr-2" />
                              <span className="text-green-300">GDPR Compliance</span>
                            </div>
                            <span className="text-green-400 font-medium">✓ PASS</span>
                          </div>
                          
                          <div className="flex items-center justify-between p-2 bg-green-900/30 rounded border border-green-700">
                            <div className="flex items-center">
                              <FileCheck size={14} className="text-green-400 mr-2" />
                              <span className="text-green-300">SOX Controls</span>
                            </div>
                            <span className="text-green-400 font-medium">✓ PASS</span>
                          </div>
                          
                          <div className="flex items-center justify-between p-2 bg-yellow-900/30 rounded border border-yellow-700">
                            <div className="flex items-center">
                              <AlertTriangle size={14} className="text-yellow-400 mr-2" />
                              <span className="text-yellow-300">PCI DSS</span>
                            </div>
                            <span className="text-yellow-400 font-medium">⚠ REVIEW</span>
                          </div>
                          
                          <div className="flex items-center justify-between p-2 bg-green-900/30 rounded border border-green-700">
                            <div className="flex items-center">
                              <FileCheck size={14} className="text-green-400 mr-2" />
                              <span className="text-green-300">HIPAA</span>
                            </div>
                            <span className="text-green-400 font-medium">✓ PASS</span>
                          </div>
                        </div>
                        
                        {/* Progress indicator */}
                        <div className="absolute bottom-2 left-2 right-2">
                          <div className="flex items-center gap-2 text-slate-400">
                            <div className="flex-1 bg-slate-600 rounded-full h-1">
                              <div className="bg-red-400 h-1 rounded-full w-[78%] animate-pulse"></div>
                            </div>
                            <span className="text-xs">Scanning... 78%</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Sidebar Compliance Stats */}
                <div className="w-full md:w-52 bg-slate-50 rounded-md p-4 space-y-2.5 ring-1 ring-slate-200 flex flex-col justify-center">
                  {[ 
                    { label: "Documents Scanned", value: complianceScannerData.scannerStats.documentsScanned, color: "text-red-700", bgColor:"bg-red-100", icon: FileCheck }, 
                    { label: "Compliance Score", value: complianceScannerData.scannerStats.complianceScore, color: "text-green-700", bgColor:"bg-green-100", icon: Shield }, 
                    { label: "Violations Found", value: complianceScannerData.scannerStats.violationsFound, color: "text-yellow-700", bgColor:"bg-yellow-100", icon: AlertTriangle }, 
                    { label: "Risk Level", value: complianceScannerData.scannerStats.riskLevel, color: "text-blue-700", bgColor:"bg-blue-100", icon: Search } 
                  ].map(stat => (
                    <div key={stat.label} className={`flex items-center justify-between p-2 rounded ${stat.bgColor}`}>
                      <div className="flex items-center">
                        <stat.icon size={14} className={`${stat.color} mr-1`} />
                        <span className={`text-xs font-medium ${stat.color}`}>{stat.label}</span>
                      </div>
                      <span className={`font-bold text-sm ${stat.color}`}>{stat.value}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="px-4 py-1.5 mt-3 bg-slate-200 border-t border-slate-300 text-xs text-slate-600 flex items-center gap-2 rounded-b-md">
                <span className="w-2 h-2 rounded-full bg-red-500 inline-block animate-pulse"></span>
                <span>Compliance scanner active. Monitoring 12,847 documents across 8 frameworks.</span>
              </div>
            </div>
          </section>

          <div className="grid md:grid-cols-1 gap-10">
            <section>
              <h2 className={`text-2xl font-semibold mb-4 ${complianceScannerData.accentColor}`}>The Challenge</h2>
              <p className="text-slate-700 leading-relaxed whitespace-pre-line">
                {complianceScannerData.challenge}
              </p>
            </section>

            <section>
              <h2 className={`text-2xl font-semibold mb-4 ${complianceScannerData.accentColor}`}>Our Solution</h2>
              <ul className="space-y-3 text-slate-700">
                {complianceScannerData.solutionHighlights.map((item, index) => (
                  <motion.li key={index} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: index * 0.1 }} className="flex items-start pl-2">
                    <CheckCircle className={`w-5 h-5 ${complianceScannerData.accentColor} mr-3 mt-0.5 flex-shrink-0`} />
                    <span>{item}</span>
                  </motion.li>
                ))}
              </ul>
            </section>

            <section>
              <h2 className={`text-2xl font-semibold mb-4 ${complianceScannerData.accentColor}`}>Key Benefits</h2>
              <div className="bg-red-50 border border-red-200 p-6 rounded-lg shadow-sm">
                <ul className="space-y-3">
                  {complianceScannerData.benefits.map((benefit, index) => (
                    <motion.li key={index} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: index * 0.1 }} className="flex items-start text-red-800">
                      <CheckCircle className="w-5 h-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span>{benefit}</span>
                    </motion.li>
                  ))}
                </ul>
              </div>
            </section>

            <section>
              <h2 className={`text-2xl font-semibold mb-5 ${complianceScannerData.accentColor}`}>Technologies Used</h2>
              <div className="flex flex-wrap gap-3">
                {complianceScannerData.techStack.map((tech, index) => (
                  <motion.span key={index} initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: index * 0.05 }}
                    className="bg-slate-200 text-slate-700 px-4 py-1.5 rounded-full text-sm font-medium border border-slate-300 hover:bg-slate-300 transition-colors cursor-default">
                    {tech}
                  </motion.span>
                ))}
              </div>
            </section>
          </div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="mt-16 text-center">
            <Link href={`/${currentLocale}/contact`} className={`${complianceScannerData.buttonColor} text-white font-semibold py-3.5 px-10 rounded-lg text-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50`}>
              Strengthen Your Compliance
            </Link>
          </motion.div>

        </motion.div>
      </main>
    </div>
  );
} 